import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  IconButton,
  Typography,
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// Transición para el modal
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function CourseDetailDialog({ open, onClose, group }) {
  const { t, i18n } = useTranslation();

  const [detailCourse, setDetailCourse] = useState(null);

  /* --------- mapear horarios --------- */
  const scheduleList = useMemo(() => {
    if (!detailCourse) return [];
    if (detailCourse.calendario) {
      const c = detailCourse.calendario;
      const rows = [];
      const add = (ok, d, from, to) =>
        ok && from && to && rows.push(`${d} ${from.slice(0, 5)} - ${to.slice(0, 5)}`);
      add(c.chklunes, 'Dilluns', c.lunesde, c.lunesa);
      add(c.chkmartes, 'Dimarts', c.martesde, c.martesa);
      add(c.chkmiercoles, 'Dimecres', c.miercolesde, c.miercolesa);
      add(c.chkjueves, 'Dijous', c.juevesde, c.juevesa);
      add(c.chkviernes, 'Divendres', c.viernesde, c.viernesa);
      add(c.chksabado, 'Dissabte', c.sabadode, c.sabadoa);
      add(c.chkdomingo, 'Diumenge', c.domingode, c.domingoa);
      return rows;
    }
    return detailCourse.horarios || [];
  }, [detailCourse]);

  const handleMatricularGroup = () => {
    if (group && group.id) {
        window.location.href = `/contact?toMatricular=true&groupId=${group.id}&lng=${i18n.language}`;
    } else {
        console.error('Group ID is missing or invalid.');
    }
  };
  const handleMatricularCourse = () => {
    if (detailCourse) window.location.href = `/contact?toMatricular=true&courseId=${detailCourse.idcurso}&lng=${i18n.language}`;
  };
  const backToGroup = () => setDetailCourse(null);

  if (!group) return null;

  /* ---------- Colores corporativos ---------- */
  const verdeClaro = '#97D9AD';
  const verdeCorporativo = '#50A579';
  const textoOscuro = '#333';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          bgcolor: verdeClaro,
          p: 0,
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      {/* -------- CABECERA -------- */}
      <Box sx={{ position: 'relative', p: 2, textAlign: 'center' }}>
        {detailCourse && (
          <IconButton
            onClick={backToGroup}
            size="small"
            sx={{ position: 'absolute', left: 8, top: 8, color: textoOscuro }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        <Typography variant="h6" sx={{ color: textoOscuro, fontWeight: 'bold' }}>
          {detailCourse ? detailCourse.descripcion : group.title}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8, color: textoOscuro }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* -------- CONTENIDO -------- */}
      <DialogContent sx={{ bgcolor: verdeClaro, pt: 0, px: 2, pb: 2 }}>
        {/* ---- VISTA GRUPO ---- */}
        {!detailCourse ? (
          <>
            <Box sx={{ mb: 2, color: textoOscuro }}>
              {/* Descripción del grupo bajo el título, solo si no hay curso detallado */}
              {!detailCourse && group.descripcion && (
                <Typography sx={{ color: textoOscuro, mt: 1, mb: 4, textAlign: 'left' }}>
                  {group.descripcion}
                </Typography>
              )}
              <Typography>
                <strong>{t('courseDetail.age')}</strong> {group.ageRequirement}
              </Typography>
            </Box>

            {/* Cursos disponibles (único bloque blanco) */}
            {group.cursos && group.cursos.length > 0 && (
              <Box sx={{ borderRadius: 1, py: 1, mb: 2 }}>
                {/* Agrupar cursos por codeje */}
                {Object.entries(
                  group.cursos.reduce((acc, curso) => {
                    const codeje = curso.codeje || t('courseDetail.others', 'Otros');
                    if (!acc[codeje]) acc[codeje] = [];
                    acc[codeje].push(curso);
                    return acc;
                  }, {})
                ).map(([codeje, cursosDelAnio]) => (
                  <Box key={codeje} sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: textoOscuro, fontWeight: 600, mb: 1, textAlign: 'center' }}
                    >
                      <strong>{t('courseDetail.coursesOfYear', { year: codeje })}</strong>
                    </Typography>
                    <Grid container spacing={1} px={1}>
                      {cursosDelAnio.map((c) => {
                        // Obtener días y franja horaria
                        let dias = [];
                        let horaInicio = null;
                        let horaFin = null;
                        if (c.calendario) {
                          const diasMap = [
                            { key: 'chklunes', label: t('days.L', 'L') },
                            { key: 'chkmartes', label: t('days.M', 'M') },
                            { key: 'chkmiercoles', label: t('days.X', 'X') },
                            { key: 'chkjueves', label: t('days.J', 'J') },
                            { key: 'chkviernes', label: t('days.V', 'V') },
                            { key: 'chksabado', label: t('days.S', 'S') },
                            { key: 'chkdomingo', label: t('days.D', 'D') },
                          ];
                          diasMap.forEach((d, idx) => {
                            if (c.calendario[d.key]) {
                              dias.push(d.label);
                              // Guardar la franja horaria del primer día encontrado
                              if (!horaInicio && !horaFin) {
                                const fromKey = [
                                  'lunesde','martesde','miercolesde','juevesde','viernesde','sabadode','domingode'][idx];
                                const toKey = [
                                  'lunesa','martesa','miercolesa','juevesa','viernesa','sabadoa','domingoa'][idx];
                                horaInicio = c.calendario[fromKey];
                                horaFin = c.calendario[toKey];
                              }
                            }
                          });
                        }
                        // Formatear la franja horaria
                        let franja = '';
                        if (horaInicio && horaFin) {
                          franja = `(${horaInicio.slice(0,5)}h - ${horaFin.slice(0,5)}h)`;
                        }
                        const horario = dias.length > 0 ? `${dias.join(', ')} - ${franja}` : c.descripcion;
                        return (
                          <Grid item xs={12} key={c.idcurso}>
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() => window.location.href = `/contact?toMatricular=true&courseId=${c.idcurso}&horario=${encodeURIComponent(horario)}&codeje=${encodeURIComponent(c.codeje || '')}&lng=${i18n.language}`}
                              sx={{
                                justifyContent: 'flex-start',
                                bgcolor: 'white',
                                color: verdeCorporativo,
                                borderColor: verdeCorporativo,
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#f7f7f7', borderColor: verdeCorporativo },
                              }}
                            >
                              <Box sx={{ width: '100%', textAlign: 'center' }}>
                                <span>{horario}</span>
                                <br />
                                <span style={{ fontSize: '0.95em', color: '#F66' }}>{t('courseDetail.availablePlaces')} {(c.plazas_restantes > 0) ? c.plazas_restantes : 1}</span>
                              </Box>
                            </Button>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}
          </>
        ) : (
          /* ---- VISTA CURSO ---- */
          <>
            <Box sx={{ mb: 2, color: textoOscuro }}>
              {/* Precios eliminados */}
            </Box>

            {scheduleList.length > 0 && (
              <Box>
                <Typography
                variant="subtitle1"
                sx={{ color: textoOscuro, fontWeight: 600, mb: 1 }}
                >
                <strong>{t('courseDetail.schedule')}</strong>
                </Typography>
                <List dense>
                  {scheduleList.map((h, i) => (
                    <ListItem key={i} sx={{ py: 0 }}>
                      <ListItemIcon sx={{ minWidth: 30, color: textoOscuro }}>
                        <CalendarMonthIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={h}
                        primaryTypographyProps={{ variant: 'body2', sx: { color: textoOscuro } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* -------- CTA -------- */}
      <DialogActions sx={{ bgcolor: verdeClaro, p: 2, justifyContent: 'center' }}>
        {detailCourse ? (
          <Button
            variant="contained"
            onClick={handleMatricularCourse}
            sx={{ bgcolor: 'white', color: verdeCorporativo, '&:hover': { bgcolor: '#f7f7f7' } }}
          >
            {t('courseDetail.enrollCourse')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleMatricularGroup}
            sx={{ bgcolor: 'white', color: verdeCorporativo, '&:hover': { bgcolor: '#f7f7f7' } }}
          >
            {t('courseDetail.enrollGroup')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
